import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Settings } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ActivityHeader } from "./ActivityHeader";
import { ChildIdentity, childThemeStyle } from "../child-theme/ChildTheme";
import SettingsSidebar from "../settings-sidebar/SettingsSidebar";
import { INITIAL_SESSION, QUIZ_SESSION_KEY, type QuizSession } from "../../hooks/use-quiz-session";
import { navigate, childPickerUrl } from "../../lib/navigation";
import type { ChildProfile } from "../../types";

export function useChildPersona(childId: string) {
  const client = useQueryClient();
  const key = ["child-persona", childId];
  const { data = "default" } = useQuery<string>({ queryKey: key, queryFn: () => "default", staleTime: Infinity });
  return [data, (value: string) => client.setQueryData(key, value)] as const;
}

/** The same identity, controls and settings flow for every child activity. */
export function ChildActivityHeader({ child, activity, otherUrl, onProfileChange, saving, saveError, onRetrySave }: {
  child: ChildProfile;
  activity: "reading" | "vocabulary";
  otherUrl?: string;
  saving?: boolean;
  saveError?: string;
  onRetrySave?: () => void;
  onProfileChange: (updates: { colorPalette?: string; emoji?: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [persona, setPersona] = useChildPersona(child.id);
  const { data: session } = useQuery<QuizSession>({ queryKey: QUIZ_SESSION_KEY, queryFn: () => INITIAL_SESSION, staleTime: Infinity, gcTime: Infinity });
  const stars = session?.rewards?.totalStars ?? 0;
  const streak = session?.streak ?? 0;
  const previousStars = useRef<number | null>(null);
  const [bump, setBump] = useState(false);
  useEffect(() => {
    if (!session?.rewards) return;
    const increased = previousStars.current !== null && stars > previousStars.current;
    previousStars.current = stars;
    if (!increased) return;
    setBump(true);
    const timer = setTimeout(() => setBump(false), 600);
    return () => clearTimeout(timer);
  }, [stars, session?.rewards]);
  function switchChild() {
    setOpen(false);
    navigate(childPickerUrl(activity));
  }
  return <>
    <ActivityHeader activity={activity} otherUrl={otherUrl} identity={
      <div className={bump ? "bump" : ""}><ChildIdentity name={child.name || "שלום!"} emoji={child.emoji}>
        {activity === "vocabulary" && stars > 0 && <span className="activity-header__rewards" title="כוכבים באוצר מילים" aria-label={`אוצר מילים: ${stars} כוכבים${streak > 1 ? `, רצף של ${streak}` : ""}`}><span aria-hidden="true">⭐{stars}</span>{streak > 1 && <span aria-hidden="true">🔥{streak}</span>}</span>}
      </ChildIdentity></div>
    }>
      <button type="button" onClick={() => setOpen(true)} aria-label="הגדרות" title="הגדרות"><Settings className="brand-icon" aria-hidden="true" strokeWidth={2.4} /></button>
    </ActivityHeader>
    {open && createPortal(<div className="child-settings child-theme" dir="rtl" style={childThemeStyle(child)}><SettingsSidebar activity={activity} saving={saving} saveError={saveError} onRetrySave={onRetrySave} childName={child.name} childGender={child.gender} gradeGroup={child.gradeGroup} step={child.currentStep} readOnly colorPalette={child.colorPalette} emoji={child.emoji} onProfileChange={onProfileChange} personaProfileId={persona} onPersonaProfileChange={setPersona} onSwitchChild={switchChild} onClose={() => setOpen(false)} /></div>, document.body)}
  </>;
}
